'use client';

import { c as _c } from "react/compiler-runtime";
import { useState, useMemo } from 'react';
import Image from 'next/image';
import { motion } from 'framer-motion';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useTranslations } from 'next-intl';
interface Product {
  name: string;
  description: string;
  photo: string;
}
interface RentalPack {
  id: string;
  name: string;
  category: string;
  description: string;
  products: Product[];
}
interface RentalData {
  adultPacks: RentalPack[];
  juniorPacks: RentalPack[];
}
interface RentalProductsCarouselProps {
  data: RentalData;
  title: string;
  subtitle: string;
  allProductsLabel: string;
  previousLabel?: string;
  nextLabel?: string;
  season?: 'summer' | 'winter';
}
export default function RentalProductsCarousel(t0) {
  const $ = _c(82);
  const {
    data,
    title,
    subtitle,
    allProductsLabel,
    previousLabel,
    nextLabel,
    season: t1
  } = t0;
  const season = t1 === undefined ? "winter" : t1;
  const t = useTranslations("Rental");
  const [selectedPack, setSelectedPack] = useState("all");
  const [currentIndex, setCurrentIndex] = useState(0);
  let products;
  if ($[0] !== data.adultPacks || $[1] !== data.juniorPacks) {
    products = [];
    [...data.adultPacks, ...data.juniorPacks].forEach(pack => {
      pack.products?.forEach(product => {
        products.push({
          ...product,
          packName: pack.name,
          packId: pack.id,
          category: pack.category
        });
      });
    });
    $[0] = data.adultPacks;
    $[1] = data.juniorPacks;
    $[2] = products;
  } else {
    products = $[2];
  }
  const allProducts = products;
  let t2;
  bb0: {
    if (selectedPack === "all") {
      t2 = allProducts;
      break bb0;
    }
    let t3;
    if ($[3] !== allProducts || $[4] !== selectedPack) {
      t3 = allProducts.filter(p => p.packId === selectedPack);
      $[3] = allProducts;
      $[4] = selectedPack;
      $[5] = t3;
    } else {
      t3 = $[5];
    }
    t2 = t3;
  }
  const filteredProducts = t2;
  let t3;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = filter => {
      setSelectedPack(filter);
      setCurrentIndex(0);
    };
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const handleFilterChange = t3;
  const itemsPerPage = season === "summer" ? 3 : 4;
  const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);
  let t4;
  if ($[7] !== totalPages) {
    t4 = () => {
      setCurrentIndex(prev => (prev + 1) % totalPages);
    };
    $[7] = totalPages;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const nextSlide = t4;
  let t5;
  if ($[9] !== totalPages) {
    t5 = () => {
      setCurrentIndex(prev_0 => prev_0 === 0 ? totalPages - 1 : prev_0 - 1);
    };
    $[9] = totalPages;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const prevSlide = t5;
  const start = currentIndex * itemsPerPage;
  let t6;
  if ($[11] !== filteredProducts || $[12] !== itemsPerPage || $[13] !== start) {
    t6 = filteredProducts.slice(start, start + itemsPerPage);
    $[11] = filteredProducts;
    $[12] = itemsPerPage;
    $[13] = start;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  const visibleProducts = t6;
  let t10;
  let t11;
  let t12;
  let t7;
  let t8;
  let t9;
  if ($[15] !== allProductsLabel || $[16] !== data.adultPacks || $[17] !== data.juniorPacks || $[18] !== selectedPack || $[19] !== subtitle || $[20] !== title) {
    const allPacks = [...data.adultPacks, ...data.juniorPacks];
    t12 = "py-16";
    t10 = "container mx-auto px-4";
    let t13;
    if ($[27] !== title) {
      t13 = <h2 className="text-5xl md:text-7xl font-bold mb-4 text-foreground font-[family-name:var(--font-bebas-neue)]">{title}</h2>;
      $[27] = title;
      $[28] = t13;
    } else {
      t13 = $[28];
    }
    let t14;
    if ($[29] !== subtitle) {
      t14 = <p className="text-xl text-muted-foreground max-w-3xl mx-auto">{subtitle}</p>;
      $[29] = subtitle;
      $[30] = t14;
    } else {
      t14 = $[30];
    }
    if ($[31] !== t13 || $[32] !== t14) {
      t11 = <div className="text-center mb-12">{t13}{t14}</div>;
      $[31] = t13;
      $[32] = t14;
      $[33] = t11;
    } else {
      t11 = $[33];
    }
    t7 = "flex flex-wrap justify-center gap-3 mb-12";
    let t15;
    if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
      t15 = () => handleFilterChange("all");
      $[34] = t15;
    } else {
      t15 = $[34];
    }
    const t16 = `px-6 py-2.5 rounded-full font-bold transition-all duration-200 ${selectedPack === "all" ? "bg-gradient-to-r from-nav-gradient-from to-nav-gradient-to text-white shadow-md" : "bg-card text-foreground/80 border border-foreground/25 hover:border-primary/40 hover:text-foreground hover:shadow-sm"}`;
    if ($[35] !== allProductsLabel || $[36] !== t16) {
      t8 = <button onClick={t15} className={t16}>{allProductsLabel}</button>;
      $[35] = allProductsLabel;
      $[36] = t16;
      $[37] = t8;
    } else {
      t8 = $[37];
    }
    t9 = allPacks.map(pack_0 => <button key={pack_0.id} onClick={() => handleFilterChange(pack_0.id)} className={`px-6 py-2.5 rounded-full font-bold transition-all duration-200 ${selectedPack === pack_0.id ? "bg-gradient-to-r from-nav-gradient-from to-nav-gradient-to text-white shadow-md" : "bg-card text-foreground/80 border border-foreground/25 hover:border-primary/40 hover:text-foreground hover:shadow-sm"}`}>{pack_0.name.replace(" Pack", "").replace(" pack", "").replace("Pack ", "").replace("pack ", "").split(" ").map(_temp).join(" ")}</button>);
    $[15] = allProductsLabel;
    $[16] = data.adultPacks;
    $[17] = data.juniorPacks;
    $[18] = selectedPack;
    $[19] = subtitle;
    $[20] = title;
    $[21] = t10;
    $[22] = t11;
    $[23] = t12;
    $[24] = t7;
    $[25] = t8;
    $[26] = t9;
  } else {
    t10 = $[21];
    t11 = $[22];
    t12 = $[23];
    t7 = $[24];
    t8 = $[25];
    t9 = $[26];
  }
  let t13;
  if ($[38] !== t7 || $[39] !== t8 || $[40] !== t9) {
    t13 = <div className={t7}>{t8}{t9}</div>;
    $[38] = t7;
    $[39] = t8;
    $[40] = t9;
    $[41] = t13;
  } else {
    t13 = $[41];
  }
  let t14;
  if ($[42] !== filteredProducts.length || $[43] !== itemsPerPage || $[44] !== nextLabel || $[45] !== nextSlide || $[46] !== prevSlide || $[47] !== previousLabel) {
    t14 = filteredProducts.length > itemsPerPage && <><button onClick={prevSlide} className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 z-10 bg-card rounded-full p-3 shadow-lg hover:shadow-xl transition-all duration-200 hover:scale-105 border border-border" aria-label={previousLabel || "Previous products"}><ChevronLeft className="w-6 h-6 text-primary" /></button><button onClick={nextSlide} className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 z-10 bg-card rounded-full p-3 shadow-lg hover:shadow-xl transition-all duration-200 hover:scale-105 border border-border" aria-label={nextLabel || "Next products"}><ChevronRight className="w-6 h-6 text-primary" /></button></>;
    $[42] = filteredProducts.length;
    $[43] = itemsPerPage;
    $[44] = nextLabel;
    $[45] = nextSlide;
    $[46] = prevSlide;
    $[47] = previousLabel;
    $[48] = t14;
  } else {
    t14 = $[48];
  }
  const t15 = `${selectedPack}-${currentIndex}`;
  let t16;
  let t17;
  let t18;
  let t19;
  if ($[49] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = {
      x: 100,
      opacity: 0
    };
    t17 = {
      x: 0,
      opacity: 1
    };
    t18 = {
      x: -100,
      opacity: 0
    };
    t19 = {
      type: "spring",
      stiffness: 300,
      damping: 30
    };
    $[49] = t16;
    $[50] = t17;
    $[51] = t18;
    $[52] = t19;
  } else {
    t16 = $[49];
    t17 = $[50];
    t18 = $[51];
    t19 = $[52];
  }
  let t20;
  if ($[53] !== season || $[54] !== visibleProducts) {
    t20 = visibleProducts.map((product_0, idx) => <div key={`${product_0.packId}-${product_0.name}-${idx}`} className={`group relative bg-card border border-border rounded-2xl overflow-hidden hover:shadow-xl transition-all duration-300 hover:-translate-y-1 ${season === "summer" ? "w-full sm:w-[calc(50%_-_0.5rem)] md:w-[calc(50%_-_0.75rem)] lg:w-[calc(33.333%_-_1rem)]" : "w-full sm:w-[calc(50%_-_0.5rem)] md:w-[calc(33.333%_-_1rem)] lg:w-[calc(25%_-_1.125rem)]"}`}><div className={`relative overflow-hidden bg-muted ${season === "summer" ? "aspect-square" : "aspect-[2/3]"}`}><Image src={product_0.photo} alt={product_0.name} fill={true} className="object-cover object-top transition-transform duration-500 group-hover:scale-105" sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw" /><div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" /><div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-10"><span className="inline-block rounded-full bg-[url('/leather.jpg')] bg-cover bg-center px-4 py-1.5 sm:px-5 sm:py-2 text-xs sm:text-sm font-semibold text-black border border-[#2a1f10]/50 uppercase tracking-wider shadow-lg">{product_0.packName.replace(" Pack", "").replace(" pack", "").replace("Pack ", "").replace("pack ", "").split(" ").map(_temp2).join(" ")}</span></div></div><div className="p-6 text-center"><h3 className="text-xl font-bold mb-3 text-foreground group-hover:text-primary transition-colors duration-300">{product_0.name}</h3><p className="text-muted-foreground leading-relaxed line-clamp-3">{product_0.description}</p></div></div>);
    $[53] = season;
    $[54] = visibleProducts;
    $[55] = t20;
  } else {
    t20 = $[55];
  }
  let t21;
  if ($[56] !== t15 || $[57] !== t20) {
    t21 = <div className="overflow-hidden px-2"><motion.div key={t15} initial={t16} animate={t17} exit={t18} transition={t19} className="flex flex-wrap justify-center gap-4 md:gap-6">{t20}</motion.div></div>;
    $[56] = t15;
    $[57] = t20;
    $[58] = t21;
  } else {
    t21 = $[58];
  }
  let t22;
  if ($[59] !== currentIndex || $[60] !== filteredProducts.length || $[61] !== itemsPerPage || $[62] !== totalPages) {
    t22 = filteredProducts.length > itemsPerPage && <div className="flex justify-center gap-2 mt-8">{Array.from({
        length: totalPages
      }).map((_, idx_0) => <button key={idx_0} onClick={() => setCurrentIndex(idx_0)} className={`transition-all duration-200 rounded-full ${currentIndex === idx_0 ? "w-8 h-3 bg-primary" : "w-3 h-3 bg-muted-foreground/30 hover:bg-muted-foreground/50"}`} aria-label={`Go to slide ${idx_0 + 1}`} />)}</div>;
    $[59] = currentIndex;
    $[60] = filteredProducts.length;
    $[61] = itemsPerPage;
    $[62] = totalPages;
    $[63] = t22;
  } else {
    t22 = $[63];
  }
  let t23;
  if ($[64] !== t14 || $[65] !== t21 || $[66] !== t22) {
    t23 = <div className="relative">{t14}{t21}{t22}</div>;
    $[64] = t14;
    $[65] = t21;
    $[66] = t22;
    $[67] = t23;
  } else {
    t23 = $[67];
  }
  let t24;
  if ($[68] !== filteredProducts.length || $[69] !== t) {
    t24 = t("productCount", {
      count: filteredProducts.length
    });
    $[68] = filteredProducts.length;
    $[69] = t;
    $[70] = t24;
  } else {
    t24 = $[70];
  }
  let t25;
  if ($[71] !== t24) {
    t25 = <div className="text-center mt-8"><p className="text-muted-foreground text-sm">{t24}</p></div>;
    $[71] = t24;
    $[72] = t25;
  } else {
    t25 = $[72];
  }
  let t26;
  if ($[73] !== t10 || $[74] !== t11 || $[75] !== t13 || $[76] !== t23 || $[77] !== t25) {
    t26 = <div className={t10}>{t11}{t13}{t23}{t25}</div>;
    $[73] = t10;
    $[74] = t11;
    $[75] = t13;
    $[76] = t23;
    $[77] = t25;
    $[78] = t26;
  } else {
    t26 = $[78];
  }
  let t27;
  if ($[79] !== t12 || $[80] !== t26) {
    t27 = <section className={t12}>{t26}</section>;
    $[79] = t12;
    $[80] = t26;
    $[81] = t27;
  } else {
    t27 = $[81];
  }
  return t27;
}
function _temp2(word_0) {
  return word_0.charAt(0).toUpperCase() + word_0.slice(1).toLowerCase();
}
function _temp(word) {
  return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
}